<template>
  <div
    ref="container"
    class="json-lottie"
    :style="{ width: props.width, height: props.height }"
    aria-hidden="true"
  ></div>
</template>

<script setup lang="ts">
import type { AnimationItem } from 'lottie-web'

const props = withDefaults(
  defineProps<{
    animationData?: Record<string, unknown> | string
    autoplay?: boolean
    height?: string
    loop?: boolean
    width?: string
  }>(),
  {
    animationData: undefined,
    autoplay: true,
    height: '10px',
    loop: true,
    width: '10px',
  },
)

const containerRef = useTemplateRef<HTMLDivElement>('container')
let animation: AnimationItem | null = null
let renderRequestId = 0

const isRemoteJsonUrl = (value: string) => /^https?:\/\/.+\.json(?:[?#].*)?$/i.test(value)

const isAnimationPayload = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const resolveAnimationSource = () => {
  if (!props.animationData) return null
  if (typeof props.animationData === 'object') {
    return { animationData: props.animationData }
  }

  const rawValue = props.animationData.trim()
  if (!rawValue) return null

  if (isRemoteJsonUrl(rawValue)) {
    return { path: rawValue }
  }

  try {
    const parsed = JSON.parse(rawValue) as unknown
    return isAnimationPayload(parsed) ? { animationData: parsed } : null
  } catch {
    return null
  }
}

const destroyAnimation = () => {
  animation?.destroy()
  animation = null
}

const renderAnimation = async () => {
  const requestId = ++renderRequestId
  const container = containerRef.value
  const animationSource = resolveAnimationSource()

  destroyAnimation()
  if (requestId !== renderRequestId) return
  if (!container || !animationSource) return

  const { default: lottie } = await import('lottie-web')
  animation = lottie.loadAnimation({
    autoplay: props.autoplay,
    container,
    loop: props.loop,
    renderer: 'svg',
    ...animationSource,
  })
}

onMounted(() => {
  void renderAnimation()
})

watch(
  () => props.animationData,
  () => {
    void renderAnimation()
  },
)

watch(
  () => [props.autoplay, props.loop, props.width, props.height],
  () => {
  void renderAnimation()
  },
)

onBeforeUnmount(() => {
  renderRequestId++
  destroyAnimation()
})
</script>

<style scoped lang="less">
.json-lottie {
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(svg) {
    display: block;
    width: 100%;
    height: 100%;
  }
}
</style>
